import { formatDistanceToNowStrict, parseISO } from "date-fns";
import {
  Clock3,
  Eye,
  EyeOff,
  Loader2,
  Pause,
  PencilLine,
  Play,
  Plus,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Badge } from "../../../components/ui/badge";
import { Button, buttonVariants } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Textarea } from "../../../components/ui/textarea";
import {
  queryClient,
  useWorkspaceSkills,
  useToggleSkill,
  useDeleteSkill,
} from "../../../hooks/QueryHooks";
import { cn } from "../../../lib/utils";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import notify from "../../../utils/notify";
import useSkillsStore from "../../../store/skills/skillsStore";
import type { CustomSkill } from "../../../services/skills/skillsEndpoints.interface";
import SkillChatBuilder from "./skill-ai-builder/components/SkillChatBuilder";
import type { SkillBuilderDraftConfig, StructuredOutputField } from "./skill-ai-builder/hooks/useSkillBuilderStream";

const kindLabel: Record<string, string> = {
  capability: "Capability",
  playbook: "Playbook",
};

const formatRelativeTime = (value?: string) => {
  if (!value) return "just now";
  try {
    return formatDistanceToNowStrict(parseISO(value), { addSuffix: true });
  } catch {
    return value;
  }
};

const SkillsPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're on a nested route (skill editor: /skills/:skillId or /skills/new)
  const pathAfterSkills = location.pathname.split("/skills")[1] || "";
  const isNestedRoute = pathAfterSkills.length > 1; // more than just "/"

  const [view, setView] = useState<"list" | "builder">("list");
  const listSearch = useSkillsStore((s) => s.listSearch);
  const setListSearch = useSkillsStore((s) => s.setListSearch);
  const listStatusFilter = useSkillsStore((s) => s.listStatusFilter);
  const setListStatusFilter = useSkillsStore((s) => s.setListStatusFilter);

  const [externalPrompt, setExternalPrompt] = useState<string | null>(null);
  const [draftSkillConfig, setDraftSkillConfig] = useState<SkillBuilderDraftConfig | null>(null);

  const { data: skills, isLoading }: any = useWorkspaceSkills({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const toggleSkillMutation = useToggleSkill();
  const deleteSkillMutation = useDeleteSkill();
  const [skillToDelete, setSkillToDelete] = useState<CustomSkill | null>(null);

  const filteredSkills = useMemo(() => {
    const source: CustomSkill[] = Array.isArray(skills) ? skills : [];
    return source
      .filter((skill) => {
        if (listStatusFilter === "all") return true;
        return skill.status === listStatusFilter;
      })
      .filter((skill) => {
        const term = listSearch.trim().toLowerCase();
        if (!term) return true;
        return `${skill.skill_name} ${skill.display_name} ${skill.description}`
          .toLowerCase()
          .includes(term);
      })
      .sort((a, b) => {
        const aDate = Date.parse(a.updated_at || a.created_at || "") || 0;
        const bDate = Date.parse(b.updated_at || b.created_at || "") || 0;
        return bDate - aDate;
      });
  }, [skills, listStatusFilter, listSearch]);

  const handleToggle = (skill: CustomSkill) => {
    toggleSkillMutation.mutate(
      { workspace_id: workspace?.id, skill_id: skill.id, enabled: !skill.is_enabled },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["skills", workspace?.id] });
          notify({ type: "success", messageList: [`Skill ${skill.is_enabled ? "disabled" : "enabled"}.`] });
        },
        onError: () => {
          notify({ type: "failure", messageList: ["Failed to toggle skill."] });
        },
      },
    );
  };

  const handleDelete = () => {
    if (!skillToDelete) return;
    deleteSkillMutation.mutate(
      { workspace_id: workspace?.id, skill_id: skillToDelete.id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["skills", workspace?.id] });
          notify({ type: "success", messageList: ["Skill deleted."] });
          setSkillToDelete(null);
        },
        onError: () => {
          notify({ type: "failure", messageList: ["Failed to delete skill."] });
        },
      },
    );
  };

  // ── Draft helpers ──
  const handleDraftFieldChange = useCallback(
    (field: keyof SkillBuilderDraftConfig, value: any) => {
      setDraftSkillConfig((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleAddKeyword = useCallback(
    (keyword: string) => {
      const trimmed = keyword.trim();
      if (!trimmed) return;
      setDraftSkillConfig((prev) => {
        const existing = prev?.keywords || [];
        if (existing.includes(trimmed)) return prev;
        return { ...prev, keywords: [...existing, trimmed] };
      });
    },
    [],
  );

  const handleRemoveKeyword = useCallback(
    (keyword: string) => {
      setDraftSkillConfig((prev) => ({
        ...prev,
        keywords: (prev?.keywords || []).filter((k) => k !== keyword),
      }));
    },
    [],
  );

  const handleAddStructuredField = useCallback(() => {
    setDraftSkillConfig((prev) => {
      const existing = prev?.structuredOutput || [];
      return {
        ...prev,
        structuredOutput: [
          ...existing,
          { fieldName: "", fieldType: "string" as const, description: "", required: false },
        ],
      };
    });
  }, []);

  const handleUpdateStructuredField = useCallback(
    (index: number, updates: Partial<StructuredOutputField>) => {
      setDraftSkillConfig((prev) => {
        const fields = [...(prev?.structuredOutput || [])];
        fields[index] = { ...fields[index], ...updates };
        return { ...prev, structuredOutput: fields };
      });
    },
    [],
  );

  const handleRemoveStructuredField = useCallback(
    (index: number) => {
      setDraftSkillConfig((prev) => {
        const fields = [...(prev?.structuredOutput || [])];
        fields.splice(index, 1);
        return { ...prev, structuredOutput: fields };
      });
    },
    [],
  );

  // Delete confirmation dialog (shared across views)
  const deleteDialog = (
    <AlertDialog
      open={!!skillToDelete}
      onOpenChange={(open) => {
        if (!open) setSkillToDelete(null);
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Skill</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete &quot;{skillToDelete?.display_name || skillToDelete?.skill_name}&quot;? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "default" })}
            onClick={handleDelete}
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  // If on a nested route (skill editor), render the Outlet
  if (isNestedRoute) {
    return (
      <div className="flex h-full w-full flex-col overflow-hidden">
        <Outlet />
      </div>
    );
  }

  // ── List View ──
  if (view === "list") {
    return (
      <div className="flex h-full min-h-0 flex-col bg-background">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-muted p-2">
              <Sparkles className="h-5 w-5 text-muted-foreground" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">Skills</h1>
              <p className="text-sm text-muted-foreground">
                Teach your AI agent custom capabilities and playbooks
              </p>
            </div>
          </div>
          <Button
            onClick={() => {
              setDraftSkillConfig(null);
              setExternalPrompt("I want to create a new skill from scratch.");
              setView("builder");
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            New Skill
          </Button>
        </div>

        {/* Filter bar */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <Tabs
            value={listStatusFilter}
            onValueChange={(v) => setListStatusFilter(v as typeof listStatusFilter)}
          >
            <TabsList>
              <TabsTrigger value="all">All Skills</TabsTrigger>
              <TabsTrigger value="active">Active</TabsTrigger>
              <TabsTrigger value="draft">Draft</TabsTrigger>
              <TabsTrigger value="archived">Archived</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
              placeholder="Search skills..."
              className="pl-9"
            />
          </div>
        </div>

        {/* Skill cards */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSkills.length ? (
            <div className="mx-auto max-w-4xl space-y-4 p-6">
              {filteredSkills.map((skill) => {
                const isDisabled = !skill.is_enabled;
                return (
                  <div
                    key={skill.id}
                    className="group cursor-pointer rounded-lg border border-border bg-background p-5 transition-all hover:border-foreground/20"
                    onClick={() => navigate(`${skill.id}`)}
                  >
                    <div className="flex items-start gap-4">
                      <div className="rounded-lg bg-muted p-2.5">
                        <Sparkles className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="mb-1 font-medium text-foreground transition-colors group-hover:text-primary">
                          {skill.display_name || skill.skill_name}
                        </h3>
                        <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                          {skill.description || "No description"}
                        </p>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline">
                            {kindLabel[skill.kind] || skill.kind}
                          </Badge>
                          <div className="flex items-center gap-1.5">
                            {isDisabled ? (
                              <Pause className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <div className="h-2 w-2 rounded-full bg-foreground" />
                            )}
                            <span className="text-xs capitalize text-muted-foreground">
                              {isDisabled ? "disabled" : skill.status}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">v{skill.version}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-end justify-between self-stretch">
                        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); navigate(`${skill.id}`); }}
                          >
                            <PencilLine className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title={isDisabled ? "Enable" : "Disable"}
                            disabled={toggleSkillMutation.isLoading}
                            onClick={(e) => { e.stopPropagation(); handleToggle(skill); }}
                          >
                            {isDisabled ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 w-8 p-0"
                            title="Delete"
                            disabled={deleteSkillMutation.isLoading}
                            onClick={(e) => { e.stopPropagation(); setSkillToDelete(skill); }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock3 className="h-3 w-3" />
                          <span>{formatRelativeTime(skill.updated_at || skill.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="mb-4 rounded-full bg-muted p-4">
                <Sparkles className="h-8 w-8 text-muted-foreground" />
              </div>
              <p className="text-lg font-medium text-foreground">No skills found</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {listSearch || listStatusFilter !== "all"
                  ? "Try changing filters or search criteria."
                  : "Create your first skill to get started."}
              </p>
              {!listSearch && listStatusFilter === "all" && (
                <Button
                  onClick={() => {
                    setDraftSkillConfig(null);
                    setExternalPrompt("I want to create a new skill from scratch.");
                    setView("builder");
                  }}
                  className="mt-4"
                >
                  <Plus className="mr-1.5 h-4 w-4" />
                  New Skill
                </Button>
              )}
            </div>
          )}
        </div>

        {deleteDialog}
      </div>
    );
  }

  // ── Builder View ──
  return (
    <div className="h-full min-h-0 bg-background">
      <div className="flex h-full min-h-0">
        {/* Left: Chat */}
        <div className="min-w-0 flex-1">
          <SkillChatBuilder
            showBackButton={true}
            onBack={() => setView("list")}
            title="Skills with ORA"
            subtitle="Create and refine skills through chat."
            externalPrompt={externalPrompt}
            onExternalPromptConsumed={() => setExternalPrompt(null)}
            draftSkillConfig={draftSkillConfig}
            onDraftSkillConfigChange={(nextDraft) => setDraftSkillConfig(nextDraft)}
          />
        </div>

        {/* Right: Draft preview sidebar */}
        <aside className="flex h-full w-96 shrink-0 flex-col border-l border-border bg-background">
          {/* Sidebar header */}
          <div className="border-b border-border p-4">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-muted p-1.5">
                <Sparkles className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-foreground">Skill Preview</h2>
                <p className="text-xs text-muted-foreground">Configure your skill draft</p>
              </div>
            </div>
          </div>

          {/* Sidebar content */}
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            {/* Display Name */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Display Name</label>
              <Input
                value={draftSkillConfig?.displayName || ""}
                onChange={(e) => handleDraftFieldChange("displayName", e.target.value)}
                placeholder="e.g. Churn Risk Analysis"
                className="h-8 text-xs"
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Description</label>
              <Textarea
                value={draftSkillConfig?.description || ""}
                onChange={(e) => handleDraftFieldChange("description", e.target.value)}
                placeholder="What does this skill do?"
                className="min-h-[56px] resize-none text-xs bg-card border-border-chip"
                rows={2}
              />
            </div>

            {/* Kind */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Kind</label>
              <Select
                value={draftSkillConfig?.kind || "capability"}
                onValueChange={(val) => handleDraftFieldChange("kind", val)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="capability">Capability</SelectItem>
                  <SelectItem value="playbook">Playbook</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Instructions */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                Instructions
                {draftSkillConfig?.instructionsContent && (
                  <span className="ml-1 text-foreground">
                    ({Math.ceil((draftSkillConfig.instructionsContent.length) / 100)} lines)
                  </span>
                )}
              </label>
              <Textarea
                value={draftSkillConfig?.instructionsContent || ""}
                onChange={(e) => handleDraftFieldChange("instructionsContent", e.target.value)}
                placeholder="Skill instructions will appear here after generation..."
                className="min-h-[120px] resize-y text-xs font-mono bg-card border-border-chip"
                rows={6}
              />
            </div>

            {/* Visibility */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Visibility</label>
              <Select
                value={draftSkillConfig?.visibility || "workspace"}
                onValueChange={(val) => handleDraftFieldChange("visibility", val)}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="workspace">
                    <span className="flex items-center gap-1.5">
                      <Eye className="h-3 w-3" />
                      Workspace
                    </span>
                  </SelectItem>
                  <SelectItem value="private">
                    <span className="flex items-center gap-1.5">
                      <EyeOff className="h-3 w-3" />
                      Private
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                {(draftSkillConfig?.visibility || "workspace") === "private"
                  ? "Only you can use this skill in chat. Not available in signals."
                  : "Available for signals, automations, and all workspace members."}
              </p>
            </div>

            {/* Keywords */}
            <div>
              <label className="mb-1 block text-[11px] font-medium text-muted-foreground">Keywords</label>
              <div className="flex flex-wrap gap-1.5">
                {(draftSkillConfig?.keywords || []).map((kw) => (
                  <Badge key={kw} variant="secondary" className="gap-1 text-[11px]">
                    {kw}
                    <button
                      type="button"
                      onClick={() => handleRemoveKeyword(kw)}
                      className="ml-0.5 rounded-full hover:bg-muted"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Input
                placeholder="Add keyword and press Enter"
                className="mt-1.5 h-8 text-xs"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddKeyword((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = "";
                  }
                }}
              />
            </div>

            {/* Structured Output */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <label className="text-[11px] font-medium text-muted-foreground">
                  Structured Output
                  {(draftSkillConfig?.structuredOutput?.length || 0) > 0 && (
                    <span className="ml-1 text-foreground">
                      ({draftSkillConfig?.structuredOutput?.length})
                    </span>
                  )}
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 px-2 text-[11px]"
                  onClick={handleAddStructuredField}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Add Field
                </Button>
              </div>
              <div className="space-y-2">
                {(draftSkillConfig?.structuredOutput || []).map((field, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border border-border bg-muted/30 p-2 space-y-1.5"
                  >
                    <div className="flex items-center gap-1.5">
                      <Input
                        value={field.fieldName}
                        onChange={(e) =>
                          handleUpdateStructuredField(idx, { fieldName: e.target.value })
                        }
                        placeholder="Field name"
                        className="h-7 flex-1 text-xs"
                      />
                      <Select
                        value={field.fieldType}
                        onValueChange={(val) =>
                          handleUpdateStructuredField(idx, {
                            fieldType: val as StructuredOutputField["fieldType"],
                          })
                        }
                      >
                        <SelectTrigger className="h-7 w-24 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">String</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="boolean">Boolean</SelectItem>
                          <SelectItem value="array">Array</SelectItem>
                          <SelectItem value="object">Object</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        type="button"
                        onClick={() => handleRemoveStructuredField(idx)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <Input
                      value={field.description}
                      onChange={(e) =>
                        handleUpdateStructuredField(idx, { description: e.target.value })
                      }
                      placeholder="Field description"
                      className="h-7 text-xs"
                    />
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={field.required}
                        onChange={(e) =>
                          handleUpdateStructuredField(idx, { required: e.target.checked })
                        }
                        className="h-3 w-3"
                      />
                      Required
                    </label>
                  </div>
                ))}
                {!(draftSkillConfig?.structuredOutput?.length) && (
                  <div className="rounded-md border border-border bg-muted/30 p-2">
                    <p className="text-xs text-muted-foreground">
                      No output fields defined. ORA can suggest them, or click Add Field.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar footer */}
          <div className="border-t border-border p-4">
            <Button
              size="sm"
              variant="default"
              className="h-8 w-full text-[12px]"
              onClick={() => {
                setExternalPrompt(
                  "I updated the skill configuration in the right sidebar. Please use the latest sidebar draft (display name, description, kind, keywords, and structured output) and continue.",
                );
              }}
              disabled={!draftSkillConfig?.displayName && !draftSkillConfig?.instructionsContent}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Send Draft to ORA
            </Button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default SkillsPage;
